import { BalanceData, GearTarget, OfferItem, RARITY_IDS, itemOdds } from "@highmarch/domain";
import { closeBtnHtml, ribbonHtml } from "../kit";
import { checkHtml, rowHtml, wellHtml } from "../components";
import { gearIconUrl } from "../portraits";
import { ItemTile, TileNames, itemTile, itemTiles } from "./dealsModel";
import { wellItem, wellOf, wellsOf } from "./dealsTiles";
import { SHOP_SKIN } from "../shopSkin";

/**
 * The Deals choosers: the pick sheet (a Custom Chest "+" slot), the gear-slot chooser (gear "you pick the slot"), the
 * Daily Deals theme picker and the odds sheet (Apple 3.1.1: odds before paying, for every random item; the Shops use
 * it too). Each is the game's parchment dialog over the scrim — a gold ribbon title, the ✕, and cream rows with the
 * item in a well (design system v2 rowHtml / wellHtml) — closed by its ✕, the scrim or a choice. Markup only: the
 * caller applies the choice through @highmarch/app Deals and redraws.
 */

const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" })[c]!);

interface ChooserRow {
  /** The row's lead (a well), title and sub-line; `end` a check or a note. */
  readonly lead: string;
  readonly title: string;
  readonly sub?: string;
  readonly end?: string;
  /** Extra markup under the title (the theme's items). */
  readonly more?: string;
  readonly on?: boolean;
  readonly disabled?: boolean;
}

const CHECK = checkHtml("dchk");

/** A chooser panel: resolves the chosen row's index, or null when closed without a choice. */
function openChooser(parent: HTMLElement, title: string, sub: string, rows: readonly ChooserRow[], cls = "", body = ""): Promise<number | null> {
  return new Promise((resolve) => {
    const scrim = document.createElement("div");
    scrim.className = `scrim dchooser ${SHOP_SKIN} ${cls}`;
    const list = rows.map((r, i) => rowHtml({
      lead: r.lead, title: r.title, sub: `${r.sub ?? ""}${r.more ?? ""}`, end: r.end, tap: true,
      className: `crow${r.on ? " on" : ""}${r.disabled ? " off" : ""}`, data: `data-i="${i}"${r.disabled ? ` aria-disabled="true"` : ""}${r.on ? ` aria-current="true"` : ""}`,
    })).join("");
    scrim.innerHTML = `<div class="panel dialog dchoose" role="dialog" aria-label="${esc(title)}">${ribbonHtml(title, "", "dtitle disp")}${closeBtnHtml()}
      ${sub ? `<p class="csub">${sub}</p>` : ""}<div class="crows">${list}${body}</div></div>`;
    const done = (v: number | null) => { scrim.remove(); resolve(v); };
    scrim.querySelector<HTMLButtonElement>(".close")!.onclick = () => done(null);
    scrim.onclick = (e) => { if (e.target === scrim) done(null); };
    for (const b of scrim.querySelectorAll<HTMLButtonElement>(".crow")) b.onclick = () => { if (!b.classList.contains("off")) done(Number(b.dataset.i)); };
    parent.append(scrim);
  });
}

function itemRow(t: ItemTile, on: boolean): ChooserRow {
  return { lead: wellOf(t, "sm", "span"), title: esc(t.name), sub: esc(t.detail), end: on ? CHECK : "", on };
}

/** A Custom Chest pick: every choice with what it is at the player's level. Resolves the choice index. */
export function choosePick(parent: HTMLElement, balance: BalanceData, level: number, choices: readonly OfferItem[], current: number, names: TileNames, slot: number): Promise<number | null> {
  const rows = choices.map((it, i) => itemRow(itemTile(balance, it, level, -1, names), i === current));
  return openChooser(parent, `PICK ${slot + 1} OF 3`, "Fill this slot with what you need most. Change it any time before you buy.", rows, "picks");
}

/** Gear "you pick the slot": the eight slots with their painted medallion at the item's rarity. Resolves the slot index. */
export function chooseGearSlot(parent: HTMLElement, balance: BalanceData, rarity: number, current: number): Promise<number | null> {
  const r = RARITY_IDS[rarity] ?? "common";
  const rows = balance.gear.slots.map((s, i): ChooserRow => ({
    on: i === current, end: i === current ? CHECK : "",
    lead: wellHtml({ art: gearIconUrl(s.id, r), rarity, size: "sm", tag: "span" }), title: esc(s.name), sub: s.target === GearTarget.Base ? "Fortress gear" : "Tower gear",
  }));
  return openChooser(parent, "CHOOSE THE SLOT", "It arrives for this slot, at your level, bound to you.", rows, "slots");
}

/** The Daily Deals theme picker: each theme's three tiers' items. Resolves the theme index. */
export function chooseTheme(parent: HTMLElement, balance: BalanceData, level: number, themes: readonly { id: string; name: string; tiers: readonly (readonly OfferItem[])[] }[], current: string, names: TileNames): Promise<number | null> {
  const rows = themes.map((t): ChooserRow => ({
    on: t.id === current, disabled: t.id === current, lead: "", title: esc(t.name), end: t.id === current ? `<small class="dcur">Today</small>` : "",
    more: `<span class="ctiles">${wellsOf(itemTiles(balance, t.tiers.flat(), level, [], names), "sm", "span")}</span>`,
  }));
  return openChooser(parent, "CHANGE DEALS", "Pick today's theme. One change a day before you buy, more with VIP.", rows, "themes");
}

/** One random item's odds (owner 2026-10-21: shown in the item's focus view and the buy confirm, no ODDS button). */
export function oddsHtml(balance: BalanceData, it: OfferItem): string {
  const odds = itemOdds(balance, it);
  if (!odds) return "";
  const bars = odds.rarityPercent.map((p, r) => (p > 0 ? `<span class="orow2"><b class="ri-${RARITY_IDS[r]}">${RARITY_IDS[r]}</b><span class="obar"><i style="width:${Math.max(2, p)}%" class="ri-${RARITY_IDS[r]}"></i></span><em>${p}%</em>`
    + `<small>${gearLine(odds.gearCount[r])}${odds.speedUpMinutes[r] ? ` · ${fmtSpeed(odds.speedUpMinutes[r])} speed-up` : ""}</small></span>` : "")).join("");
  const gearOnly = it.kind === "gear";
  return `<div class="dodd2">${gearOnly ? "" : `<span class="obars">${bars}</span>`}`
    + `<small class="ofoot">Slot: any of ${odds.slots}, equal odds · Item level: yours, or up to ${odds.levelReqSpread} below${gearOnly ? "" : " · Gold and speed-up set by rarity"}</small></div>`;
}

/** The odds sheet: for each random item, the chest's rarity odds, gear count, slot and level spread, speed-ups. */
export function showOdds(parent: HTMLElement, balance: BalanceData, level: number, offerName: string, items: readonly OfferItem[], names: TileNames): Promise<number | null> {
  const seen = new Set<string>();
  const cards: string[] = [];
  for (const it of items) {
    const key = JSON.stringify({ k: it.kind, r: "rarity" in it ? it.rarity : null, t: it.kind === "luckyChest" ? it.table : null });
    const body = oddsHtml(balance, it);
    if (!body || seen.has(key)) continue;
    seen.add(key);
    const tile = itemTile(balance, it, level, -1, names);
    cards.push(`<div class="k-card dodd"><span class="doh">${wellOf(tile, "sm", "span")}<b>${esc(tile.name)}</b></span>${body}</div>`);
  }
  return openChooser(parent, "WHAT'S INSIDE", `${esc(offerName)} · the odds on every roll`, [], "odds", cards.join(""));
}

/**
 * An item in focus (owner 2026-10-21: a tap on any item): its art large, its name and what it is, and for a chest or
 * any random roll the odds of everything it can hold. Replaces the "?" badges and the ODDS buttons.
 */
export function showItemFocus(parent: HTMLElement, balance: BalanceData, level: number, item: OfferItem, names: TileNames = {}, gearSlot = -1): Promise<number | null> {
  const t = itemTile(balance, item, level, gearSlot, names);
  const odds = oddsHtml(balance, item);
  const body = `<div class="ifocus">${wellOf(t, "lg", "span", "ifwell")}<p class="ifdet">${esc(t.detail)}</p>${odds ? `<b class="ifhead">What's inside</b>${odds}` : ""}</div>`;
  return openChooser(parent, t.name.toUpperCase(), "", [], "odds focus", body);
}

/** Binds every item well under `root` with its item to the focus view. */
export function bindItemFocus(root: ParentNode, parent: HTMLElement, balance: BalanceData, level: number, names: TileNames = {}): void {
  for (const b of root.querySelectorAll<HTMLElement>("button.dwell[data-item]:not(.picked)")) b.onclick = (e) => {
    e.stopPropagation();
    const item = wellItem(b.dataset.item);
    if (item) void showItemFocus(parent, balance, level, item, names);
  };
}

function gearLine(c: readonly [number, number] | undefined): string {
  if (!c) return "";
  const [lo, hi] = c;
  return lo === hi ? `${lo} gear item${lo === 1 ? "" : "s"}` : `${lo}–${hi} gear items`;
}
function fmtSpeed(m: number): string { return m >= 60 && m % 60 === 0 ? `${m / 60}h` : `${m}m`; }
